import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, Download, FileText, Image as ImageIcon, Eye, Trash2, Search, X } from "lucide-react";
import { api, API } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import { toast } from "sonner";
import {
    useFileCategories,
    migrateCategoryLegacy,
} from "../../contexts/FileCategoriesContext";

const WS_NAMES = { 1:"Enquiry & Qualification", 2:"Feasibility", 3:"Free Design Onboarding", 4:"Free Design Package", 5:"Project Transition", 6:"Paid Services", 7:"Post Completion" };
const WS_PILLS = [1, 2, 3, 4, 5, 6, 7, "unassigned"];
// Sort options for the table / dropdown control.  Keys mirror the
// column keys below; "asc"/"desc" is managed separately so clicking
// a header rotates the direction on that same column.
const SORT_OPTIONS = [
    { key: "updated_at",       label: "Updated (newest first)", dir: "desc" },
    { key: "updated_at",       label: "Updated (oldest first)", dir: "asc"  },
    { key: "name",             label: "Filename A–Z",           dir: "asc"  },
    { key: "name",             label: "Filename Z–A",           dir: "desc" },
    { key: "document_number",  label: "Document Number",        dir: "asc"  },
    { key: "category",         label: "Category",               dir: "asc"  },
];

function fmt(dt) { try { return new Date(dt).toLocaleString("en-GB"); } catch { return dt || "—"; } }
function bytes(n) { if (!n) return "—"; const u=["B","KB","MB","GB"]; let i=0,v=n; while(v>1024&&i<u.length-1){v/=1024;i++;} return `${v.toFixed(v<10?1:0)} ${u[i]}`; }

function TypeIcon({ mime }) {
    if ((mime||"").startsWith("image/")) return <ImageIcon className="w-4 h-4 text-[#666]" />;
    return <FileText className="w-4 h-4 text-[#666]" />;
}

function Pill({ tone, children }) {
    const map = {
        current: "bg-charcoal text-white",
        superseded: "bg-[#EBE8E0] text-[#666]",
        withdrawn: "bg-[#EDE3E1] text-[#7A5148]",
        removed: "bg-[#EDE3E1] text-[#7A5148]",
        draft: "bg-[#F5EFE7] text-[#7A6A50]",
        published: "bg-[#E8F0E7] text-[#3B5B37]",
        issued: "bg-[#DDE7F3] text-[#2E4A78]",
        default: "bg-white border border-[#E5E2DC] text-charcoal",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-manrope font-semibold uppercase tracking-wide ${map[tone]||map.default}`}>{children}</span>;
}
function versionStatePill(s){
    const t = { "Current":"current", "Superseded":"superseded", "Withdrawn":"withdrawn", "Removed by Client":"removed" }[s] || "default";
    return <Pill tone={t}>{s || "—"}</Pill>;
}
function pubStatePill(s){
    const t = { "Internal Draft":"draft", "Published":"published", "Formally Issued":"issued" }[s] || "default";
    return <Pill tone={t}>{s || "—"}</Pill>;
}

export function ProjectFiles() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { categories, byKey, adminSelectable } = useFileCategories();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    // Upload form — category now required (controlled dropdown).
    const [form, setForm] = useState({
        name:"", category:"", originating_workstage:1,
        document_number:"", notes:"", client_visible:false, file:null,
    });
    // Phase C — toolbar state. Group-by-WS defaults ON per the brief.
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [wsFilter, setWsFilter] = useState(null);
    const [groupByWs, setGroupByWs] = useState(true);
    const [sortKey, setSortKey] = useState("updated_at");
    const [sortDir, setSortDir] = useState("desc");
    // Phase D — delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    usePageMeta({ title: "Files · Project | Studio G4 Admin" });

    // Debounce the search box → `searchQ`.
    useEffect(() => {
        const t = setTimeout(() => setSearchQ(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // CATEGORY_PILLS derive from the SSOT context on each render so a
    // future added category shows up with zero frontend edits.
    const categoryPills = useMemo(() =>
        (categories || []).map((c) => ({ key: c.key, label: c.label })),
    [categories]);

    const refresh = async () => {
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({ current_only: "false" });
            if (searchQ) params.set("q", searchQ);
            // Category filter — only round-trip when the user picked a
            // real controlled key.  Legacy rows migrated client-side.
            if (categoryFilter) params.set("category", categoryFilter);
            if (wsFilter && wsFilter !== "unassigned") {
                params.set("originating_workstage", String(wsFilter));
            }
            const r = await api.get(`/admin/projects/${id}/files?${params.toString()}`);
            setItems(r.data?.items || []);
        } catch { setError("Could not load files"); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ },
        [id, searchQ, categoryFilter, wsFilter]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.file || !form.name || !form.category) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("originating_workstage", String(form.originating_workstage));
            fd.append("category", form.category);
            if (form.document_number) fd.append("document_number", form.document_number);
            if (form.notes) fd.append("notes", form.notes);
            fd.append("client_visible", String(form.client_visible));
            fd.append("create_op_id", `up-${Date.now()}-${Math.random().toString(36).slice(2,8)}`);
            fd.append("upload", form.file);
            await api.post(`/admin/projects/${id}/files`, fd);
            setForm({ name:"", category:"", originating_workstage:1, document_number:"", notes:"", client_visible:false, file:null });
            await refresh();
        } catch (err) {
            setError(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // Hydrate list — migrate any legacy `category` string into the
    // controlled key so the filter pills + group buckets display
    // consistently during the deploy window (before the retcon runs).
    const hydratedItems = useMemo(() => items.map((entry) => ({
        ...entry,
        file: {
            ...entry.file,
            category: migrateCategoryLegacy(entry.file.category),
        },
    })), [items]);

    const filteredItems = useMemo(() => hydratedItems.filter(({ file }) => {
        if (wsFilter === "unassigned" && file.originating_workstage) return false;
        return true;
    }), [hydratedItems, wsFilter]);

    const sortedItems = useMemo(() => {
        const arr = [...filteredItems];
        const getKey = (entry) => {
            const f = entry.file;
            const cur = (entry.versions || []).find((v) => v.version_state === "Current")
                        || entry.versions?.[entry.versions.length - 1];
            if (sortKey === "name") return (f.name || "").toLowerCase();
            if (sortKey === "document_number") return (cur?.document_number || "").toLowerCase();
            if (sortKey === "category") return byKey[f.category]?.label || f.category || "";
            return f.updated_at || "";
        };
        arr.sort((a, b) => {
            const ka = getKey(a); const kb = getKey(b);
            if (ka < kb) return sortDir === "asc" ? -1 : 1;
            if (ka > kb) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [filteredItems, sortKey, sortDir, byKey]);

    // Group by WS — show all 7 + Unassigned, even when a bucket is empty.
    const grouped = useMemo(() => {
        const g = new Map();
        for (const ws of [1, 2, 3, 4, 5, 6, 7, "unassigned"]) g.set(ws, []);
        for (const entry of sortedItems) {
            const ws = entry.file.originating_workstage || "unassigned";
            if (!g.has(ws)) g.set(ws, []);
            g.get(ws).push(entry);
        }
        return [1, 2, 3, 4, 5, 6, 7, "unassigned"].map((k) =>
            ({ ws: k, rows: g.get(k) }));
    }, [sortedItems]);

    const openDelete = (entry, ev) => {
        ev.stopPropagation();
        setDeleteTarget(entry);
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/projects/${id}/files/${deleteTarget.file.id}`);
            toast.success("File deleted");
            setDeleteTarget(null);
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) {
                toast.error(
                    (detail && detail.message)
                    || "Executed documents cannot be deleted — upload a new version instead.");
            } else {
                toast.error(
                    (typeof detail === "string" && detail)
                    || "Delete failed");
            }
        } finally {
            setDeleting(false);
        }
    };

    const patchFile = async (fileId, patch, { optimistic }) => {
        // Optimistic UI update — flip the field locally, call the
        // backend, revert on failure with a toast.
        setItems((prev) => prev.map((entry) => {
            if (entry.file.id !== fileId) return entry;
            const updated = { ...entry };
            if ("client_visible" in patch) {
                updated.versions = (entry.versions || []).map((v) =>
                    v.version_state === "Current"
                    ? { ...v, client_visible: patch.client_visible }
                    : v);
            }
            if ("originating_workstage" in patch) {
                updated.file = { ...entry.file,
                                  originating_workstage: patch.originating_workstage };
            }
            return updated;
        }));
        try {
            await api.patch(`/admin/projects/${id}/files/${fileId}`, patch);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.reason || "Save failed");
            // Rollback by refreshing authoritative state.
            await refresh();
        }
    };

    const resetFilters = () => {
        setSearchInput(""); setSearchQ("");
        setCategoryFilter(null); setWsFilter(null);
    };
    const filtersActive = Boolean(
        searchQ || categoryFilter || wsFilter);

    const clickHeader = (key) => {
        if (sortKey === key) {
            setSortDir((d) => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key); setSortDir("asc");
        }
    };

    return (
        <div className="flex flex-col gap-4" data-testid="files-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="files-back-to-workspace" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Project Files</h1>

            <form onSubmit={submit} className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-3" data-testid="files-upload-form">
                <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Upload new file</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input required data-testid="files-upload-name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="File name" className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px] outline-none focus:border-charcoal" />
                    <select data-testid="files-upload-workstage" value={form.originating_workstage} onChange={e=>setForm(f=>({...f,originating_workstage:parseInt(e.target.value,10)}))} className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                        {Object.entries(WS_NAMES).map(([n,name])=><option key={n} value={n}>WS {n} · {name}</option>)}
                    </select>
                    <select required data-testid="files-upload-category" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                        <option value="">Content category…</option>
                        {adminSelectable.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                    <input data-testid="files-upload-docnum" value={form.document_number} onChange={e=>setForm(f=>({...f,document_number:e.target.value}))} placeholder="Document number (optional)" className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px] outline-none focus:border-charcoal" />
                </div>
                <input required type="file" data-testid="files-upload-input" onChange={e=>setForm(f=>({...f,file:e.target.files?.[0]||null}))} className="text-[13px]" />
                <label className="inline-flex items-center gap-2 text-[13px] text-[#666]">
                    <input type="checkbox" data-testid="files-upload-client-visible" checked={form.client_visible} onChange={e=>setForm(f=>({...f,client_visible:e.target.checked}))} />
                    Client-visible on upload
                </label>
                {error && <div className="text-[12px] text-terracotta" data-testid="files-upload-error">{String(error)}</div>}
                <button type="submit" disabled={uploading||!form.file||!form.name||!form.category} data-testid="files-upload-submit" className="self-start inline-flex items-center gap-1.5 px-3 py-2 bg-charcoal text-white text-[13px] rounded-md hover:bg-black disabled:opacity-60">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
                </button>
            </form>

            {/* Toolbar — search + filter pills + sort + grouping toggle */}
            <div className="bg-white border border-[#E5E2DC] rounded-lg p-3 flex flex-col gap-3" data-testid="files-toolbar">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search className="w-4 h-4 text-[#999] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input value={searchInput} onChange={(e)=>setSearchInput(e.target.value)}
                               placeholder="Search filename, document number…"
                               data-testid="files-search"
                               className="w-full border border-[#E5E2DC] rounded-md pl-9 pr-3 py-2 text-[13px] outline-none focus:border-charcoal" />
                    </div>
                    <select data-testid="files-toolbar-sort" value={`${sortKey}:${sortDir}`}
                            onChange={(e)=>{ const [k,d]=e.target.value.split(":"); setSortKey(k); setSortDir(d); }}
                            className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                        {SORT_OPTIONS.map((o) => (
                            <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>{o.label}</option>
                        ))}
                    </select>
                    <label className="inline-flex items-center gap-2 text-[13px] text-[#666]">
                        <input type="checkbox" checked={groupByWs} onChange={(e)=>setGroupByWs(e.target.checked)}
                               data-testid="files-toolbar-group-by-ws" />
                        Group by workstage
                    </label>
                    {filtersActive && (
                        <button type="button" onClick={resetFilters}
                                data-testid="files-toolbar-reset"
                                className="inline-flex items-center gap-1 text-[12px] text-[#666] hover:text-charcoal">
                            <X className="w-3 h-3" /> Reset
                        </button>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#999]">Category</span>
                        {categoryPills.map(({ key, label }) => (
                            <button key={key} type="button"
                                    onClick={()=>setCategoryFilter(categoryFilter===key?null:key)}
                                    data-testid={`files-toolbar-cat-${key}`}
                                    className={`text-[11px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${categoryFilter===key?"bg-charcoal text-white":"bg-white border border-[#E5E2DC] text-[#666] hover:bg-[#F5F3EE]"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#999]">Workstage</span>
                        {WS_PILLS.map((n) => (
                            <button key={String(n)} type="button"
                                    onClick={()=>setWsFilter(wsFilter===n?null:n)}
                                    data-testid={`files-toolbar-ws-${n}`}
                                    className={`text-[11px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${wsFilter===n?"bg-charcoal text-white":"bg-white border border-[#E5E2DC] text-[#666] hover:bg-[#F5F3EE]"}`}>
                                {n==="unassigned"?"Unassigned":`WS${n}`}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto" data-testid="files-list">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading files…</div> :
                groupByWs ? (
                    <div className="flex flex-col divide-y divide-[#E5E2DC]" data-testid="files-list-grouped">
                        {grouped.map(({ ws, rows }) => (
                            <div key={String(ws)} data-testid={`files-group-ws-${ws}`}>
                                <div className="px-4 py-2 bg-[#F5F3EE] text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                                    {ws==="unassigned"?"Unassigned":`WS ${ws} · ${WS_NAMES[ws]||""}`}  ({rows.length})
                                </div>
                                {rows.length === 0 ? (
                                    <div className="px-4 py-3 text-[12px] text-[#999]" data-testid={`files-group-ws-${ws}-empty`}>No files in this workstage.</div>
                                ) : (
                                    <FileTable id={id} rows={rows} openDelete={openDelete}
                                                navigate={navigate} byKey={byKey}
                                                onPatch={patchFile} onHeaderClick={clickHeader}
                                                sortKey={sortKey} sortDir={sortDir} />
                                )}
                            </div>
                        ))}
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="p-4 text-[#666] text-[13px]" data-testid="files-list-empty">No files match the current view.</div>
                ) : (
                    <FileTable id={id} rows={sortedItems} openDelete={openDelete}
                                navigate={navigate} byKey={byKey}
                                onPatch={patchFile} onHeaderClick={clickHeader}
                                sortKey={sortKey} sortDir={sortDir} />
                )}
            </div>
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                     data-testid="files-delete-dialog">
                    <div className="bg-white border border-[#E5E2DC] rounded-lg max-w-md w-full mx-4 p-5 flex flex-col gap-3">
                        <div className="font-manrope font-semibold text-[16px] text-charcoal">Delete file?</div>
                        <div className="text-[13px] text-charcoal">
                            <div className="font-mono break-all" data-testid="files-delete-dialog-filename">{deleteTarget.file.name}</div>
                            <div className="text-[12px] text-[#666] mt-1">
                                Category: {byKey[deleteTarget.file.category]?.label || deleteTarget.file.category || "—"}  ·  WS{deleteTarget.file.originating_workstage || " —"}
                            </div>
                            <div className="text-[12px] text-terracotta mt-2">This cannot be undone.</div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={()=>setDeleteTarget(null)}
                                    data-testid="files-delete-dialog-cancel"
                                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#F5F3EE]">
                                Cancel
                            </button>
                            <button type="button" onClick={confirmDelete} disabled={deleting}
                                    data-testid="files-delete-dialog-confirm"
                                    className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md hover:bg-[#8A4535] disabled:opacity-50">
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function _arrow(active, dir) {
    if (!active) return "";
    return dir === "asc" ? " ↑" : " ↓";
}

function FileTable({ id, rows, openDelete, navigate, byKey, onPatch, onHeaderClick, sortKey, sortDir }) {
    return (
        <table className="w-full text-[13px] text-left whitespace-nowrap">
            <thead className="text-[#666]"><tr>
                <th onClick={()=>onHeaderClick("name")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">File{_arrow(sortKey==="name", sortDir)}</th>
                <th onClick={()=>onHeaderClick("category")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Category{_arrow(sortKey==="category", sortDir)}</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Source</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">WS</th>
                <th onClick={()=>onHeaderClick("document_number")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Document #{_arrow(sortKey==="document_number", sortDir)}</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Current V</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Version state</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Publication</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Visible to client</th>
                <th onClick={()=>onHeaderClick("updated_at")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Updated{_arrow(sortKey==="updated_at", sortDir)}</th>
                <th className="font-medium py-2 px-4 border-b border-[#E5E2DC] w-10"></th>
            </tr></thead>
            <tbody>
                {rows.map(({ file, versions }) => {
                    const cur = (versions||[]).find(v=>v.version_state==="Current") || versions?.[versions.length-1];
                    const canDelete = !file.immutable;
                    const catLabel = byKey[file.category]?.label || file.category || "—";
                    const sourceOrigin = file.source_origin;
                    return (
                        <tr key={file.id} data-testid={`file-row-${file.id}`} onClick={()=>navigate(`/admin/projects/${id}/files/${file.id}`)} className="cursor-pointer hover:bg-[#FAFAFA] border-t border-[#E5E2DC]">
                            <td className="py-2 px-4">
                                <div className="flex items-center gap-2"><TypeIcon mime={cur?.mime_type} /><span className="font-semibold">{file.name}</span></div>
                            </td>
                            <td className="py-2 px-4">{catLabel}</td>
                            <td className="py-2 px-4">{sourceOrigin
                                ? <span data-testid={`file-source-${file.id}`}
                                        className="inline-block text-[10px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F5F3EE] text-[#666]">
                                    {sourceOrigin}
                                  </span>
                                : "—"}</td>
                            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                                <select data-testid={`file-ws-select-${file.id}`}
                                        value={file.originating_workstage ?? ""}
                                        onChange={(e)=>onPatch(file.id, {
                                            originating_workstage: e.target.value === ""
                                                ? null : parseInt(e.target.value, 10),
                                        }, { optimistic: true })}
                                        className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[12px] bg-white">
                                    <option value="">—</option>
                                    {[1,2,3,4,5,6,7].map((n)=><option key={n} value={n}>WS{n}</option>)}
                                </select>
                            </td>
                            <td className="py-2 px-4">{cur?.document_number || "—"}</td>
                            <td className="py-2 px-4">v{cur?.version_number || "—"}</td>
                            <td className="py-2 px-4">{versionStatePill(cur?.version_state)}</td>
                            <td className="py-2 px-4">{pubStatePill(cur?.publication_state)}</td>
                            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                                <input type="checkbox"
                                        data-testid={`file-visible-${file.id}`}
                                        checked={Boolean(cur?.client_visible)}
                                        onChange={(e)=>onPatch(file.id, {
                                            client_visible: e.target.checked,
                                        }, { optimistic: true })} />
                            </td>
                            <td className="py-2 px-4 text-[#666]">{fmt(file.updated_at)}</td>
                            <td className="py-2 px-4">
                                {canDelete && (
                                    <button type="button"
                                            onClick={(ev)=>openDelete({ file, versions }, ev)}
                                            data-testid={`files-row-delete-${file.id}`}
                                            title="Delete file"
                                            className="text-[#999] hover:text-terracotta p-1 rounded">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}


export function FileDetail() {
    const { id, fileId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadVer, setUploadVer] = useState(null);
    const [reasonVer, setReasonVer] = useState("");
    const [issueMeta, setIssueMeta] = useState({ related_record_type:"", related_record_id:"" });
    const fileLabel = data?.file?.name || "File";
    usePageMeta({ title: `${fileLabel} · Files | Studio G4 Admin` });

    const load = async () => {
        setLoading(true); setError(null);
        try { const r = await api.get(`/admin/projects/${id}/files/${fileId}`); setData(r.data); }
        catch { setError("Could not load file"); }
        finally { setLoading(false); }
    };
    useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [id, fileId]);

    if (loading) return <div className="text-[#666]" data-testid="file-detail-loading">Loading file…</div>;
    if (error || !data) return <div data-testid="file-detail-error" className="text-[#7A5148]">{error||"File not found"}</div>;

    const versions = (data.versions||[]).slice().sort((a,b)=>b.version_number-a.version_number);
    const current = versions.find(v=>v.version_state==="Current") || versions[0];
    const previewUrl = current ? `${API}/admin/files/versions/${current.id}/preview` : null;
    const isPreviewable = (m)=>!!m && (m.startsWith("image/") || m === "application/pdf");
    const isImage = (m)=>!!m && m.startsWith("image/");
    const isPdf = (m)=>m === "application/pdf";
    const EXECUTED_CATEGORIES = ["executed_agreement", "executed_contract", "executed_change_instrument"];
    const isExecutedDoc = EXECUTED_CATEGORIES.includes(data.file.category);
    const MIN_REASON_LEN = 20;

    const doNewVersion = async () => {
        if (!uploadVer) return;
        if (isExecutedDoc && (reasonVer || "").trim().length < MIN_REASON_LEN) {
            setError(`Reason must be at least ${MIN_REASON_LEN} characters for executed documents.`);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("expected_current_version", String(current.version_number));
            fd.append("upload", uploadVer);
            if (isExecutedDoc) {
                fd.append("reason", (reasonVer || "").trim());
                await api.post(`/admin/projects/${id}/files/${fileId}/executed/new-version`, fd);
            } else {
                await api.post(`/admin/projects/${id}/files/${fileId}/versions`, fd);
            }
            setUploadVer(null); setReasonVer(""); await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d
                     : d?.error ? `${d.error}: ${d.reason || d.message || ""}`
                     : "Upload failed");
        }
        finally { setBusy(false); }
    };
    const doPublish = async () => {
        setBusy(true);
        try { await api.post(`/admin/files/${fileId}/versions/${current.id}/publish`); await load(); }
        catch(e){ setError(e?.response?.data?.detail||"Publish failed"); }
        finally{ setBusy(false); }
    };
    const doIssue = async () => {
        if (!issueMeta.related_record_type || !issueMeta.related_record_id) { setError("Related record type and id required"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("related_record_type", issueMeta.related_record_type);
            fd.append("related_record_id", issueMeta.related_record_id);
            await api.post(`/admin/files/${fileId}/versions/${current.id}/issue`, fd);
            await load();
        } catch(e){ setError(e?.response?.data?.detail||"Issue failed"); }
        finally{ setBusy(false); }
    };

    const canPublish = current && current.publication_state === "Internal Draft" && current.version_state === "Current";
    const canIssue = current && current.publication_state === "Published" && current.version_state === "Current";

    return (
        <div className="flex flex-col gap-4" data-testid="file-detail-page">
            <button onClick={()=>navigate(`/admin/projects/${id}/files`)} data-testid="file-detail-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to files
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal" data-testid="file-detail-name">{data.file.name}</h1>
                    <div className="text-[13px] text-[#666] mt-1">
                        WS {data.file.originating_workstage} · {WS_NAMES[data.file.originating_workstage]||"—"} · {data.file.category||"Uncategorised"}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {current && versionStatePill(current.version_state)}
                    {current && pubStatePill(current.publication_state)}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
                <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-hidden" data-testid="file-detail-preview">
                    {current && isPreviewable(current.mime_type) ? (
                        isImage(current.mime_type) ? (
                            <img src={previewUrl} alt={data.file.name} className="w-full max-h-[70vh] object-contain bg-[#FAFAFA]" data-testid="file-preview-image" />
                        ) : isPdf(current.mime_type) ? (
                            <iframe title={data.file.name} src={previewUrl} className="w-full h-[70vh] bg-[#FAFAFA]" data-testid="file-preview-pdf" />
                        ) : null
                    ) : (
                        <div className="p-8 text-center text-[#666]" data-testid="file-preview-unavailable">
                            <div className="mb-2"><FileText className="w-8 h-8 inline text-[#999]" /></div>
                            Preview not available for this file type. Use the download control below.
                        </div>
                    )}
                    {current && (
                        <div className="border-t border-[#E5E2DC] p-3 flex items-center gap-2">
                            <a data-testid="file-detail-download" href={`${API}/admin/files/versions/${current.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]">
                                <Download className="w-4 h-4" /> Download v{current.version_number}
                            </a>
                            {isPreviewable(current.mime_type) && (
                                <a data-testid="file-detail-open-preview" href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]">
                                    <Eye className="w-4 h-4" /> Open preview
                                </a>
                            )}
                        </div>
                    )}
                </div>

                <aside className="flex flex-col gap-4">
                    <section className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="file-detail-versions">
                        <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Version history</div>
                        <ul className="flex flex-col gap-2">
                            {versions.map(v => (
                                <li key={v.id} className="flex items-center gap-2 text-[13px]" data-testid={`file-detail-version-${v.version_number}`}>
                                    <span className="font-manrope font-semibold">v{v.version_number}</span>
                                    {versionStatePill(v.version_state)}
                                    {pubStatePill(v.publication_state)}
                                    <span className="text-[#666] text-[12px] ml-auto">{bytes(v.size)}</span>
                                    <a href={`${API}/admin/files/versions/${v.id}/download`} target="_blank" rel="noreferrer" title={`Download v${v.version_number}`} data-testid={`file-detail-version-download-${v.version_number}`} className="p-1 rounded hover:bg-[#FAFAFA]">
                                        <Download className="w-3.5 h-3.5" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2" data-testid="file-detail-actions">
                        <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Actions</div>
                        <label className="text-[12px] text-[#666]">Upload new version</label>
                        <input type="file" data-testid="file-detail-version-file" onChange={e=>setUploadVer(e.target.files?.[0]||null)} className="text-[13px]" />
                        {isExecutedDoc && (
                            <>
                                <label className="text-[12px] text-[#666] mt-1">
                                    Reason <span className="text-[11px] text-[#8A8A84]">(required · min {MIN_REASON_LEN} characters)</span>
                                </label>
                                <textarea
                                    data-testid="file-detail-version-reason"
                                    value={reasonVer}
                                    onChange={e=>setReasonVer(e.target.value)}
                                    placeholder="Why is a new version of this executed document being uploaded? e.g. original signed PDF scan unreadable; replacing with higher-resolution copy."
                                    rows={3}
                                    className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] resize-y" />
                                <div className="text-[11px] text-[#8A8A84]">
                                    {reasonVer.trim().length}/{MIN_REASON_LEN} characters.
                                    Logged against this file in the project activity trail.
                                </div>
                            </>
                        )}
                        <button
                            disabled={busy || !uploadVer || (isExecutedDoc && reasonVer.trim().length < MIN_REASON_LEN)}
                            onClick={doNewVersion}
                            data-testid="file-detail-version-submit"
                            className="self-start px-3 py-1.5 bg-charcoal text-white text-[13px] rounded-md disabled:opacity-60">
                            {isExecutedDoc ? "Upload new executed version" : "Upload new version"}
                        </button>
                        <div className="h-px bg-[#E5E2DC] my-2" />
                        <button disabled={busy||!canPublish} onClick={doPublish} data-testid="file-detail-publish" className="self-start px-3 py-1.5 border border-[#E5E2DC] text-[13px] rounded-md hover:bg-[#FAFAFA] disabled:opacity-60">Publish current version</button>
                        <div className="h-px bg-[#E5E2DC] my-2" />
                        <label className="text-[12px] text-[#666]">Formally Issue against a linked record</label>
                        <input data-testid="file-detail-issue-type" placeholder="Related record type (e.g. proposal)" value={issueMeta.related_record_type} onChange={e=>setIssueMeta(m=>({...m,related_record_type:e.target.value}))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        <input data-testid="file-detail-issue-id" placeholder="Related record id" value={issueMeta.related_record_id} onChange={e=>setIssueMeta(m=>({...m,related_record_id:e.target.value}))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        <button disabled={busy||!canIssue} onClick={doIssue} data-testid="file-detail-issue" className="self-start px-3 py-1.5 border border-[#E5E2DC] text-[13px] rounded-md hover:bg-[#FAFAFA] disabled:opacity-60">Formally Issue</button>
                    </section>

                    {data.links && data.links.length > 0 && (
                        <section className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="file-detail-links">
                            <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Linked records</div>
                            <ul className="flex flex-col gap-1 text-[13px]">
                                {data.links.map((l, i) => (
                                    <li key={i} data-testid={`file-detail-link-${i}`}>{l.relationship_type || "link"} · {l.target_type || "?"} · {l.target_id || "—"}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                    {error && <div className="text-[12px] text-terracotta" data-testid="file-detail-error-msg">{String(error)}</div>}
                </aside>
            </div>
        </div>
    );
}

export function ProjectCommunications() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    usePageMeta({ title: "Communications · Project | Studio G4 Admin" });
    // §24 — unified feed: emails + calls + notes interleaved,
    // newest first.  Reuses the merged `/comms-and-notes` endpoint
    // (no `limit` here to fetch the full page).
    useEffect(()=>{ (async ()=>{
        try{
            const r = await api.get(`/admin/projects/${id}/comms-and-notes?limit=100`);
            setItems(r.data?.items||[]);
        } finally { setLoading(false); }
    })(); }, [id]);
    const TYPE_LABEL = { email: "✉ Email", call: "☏ Call", note: "🗒 Note" };
    return (
        <div className="flex flex-col gap-4" data-testid="communications-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="communications-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Communications and Notes</h1>
            <div className="text-[12px] text-[#666] italic">Unified timeline · emails, calls and internal notes interleaved, newest first.</div>
            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading…</div> :
                items.length===0 ? <div className="p-4 text-[#666] text-[13px]" data-testid="communications-empty">No communications or notes yet.</div> :
                <ul className="flex flex-col divide-y divide-[#E5E2DC]">
                    {items.map((r, idx)=>{
                        const m = r.meta || {};
                        return (
                        <li key={r.ref_id || idx} data-testid={`comms-notes-row-${r.ref_id || idx}`} className="p-4 flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-[12px] text-[#666]">
                                <span className="uppercase font-manrope font-semibold" data-testid={`comms-notes-row-${r.ref_id || idx}-type`}>
                                    {TYPE_LABEL[r.type] || r.type}
                                </span>
                                {m.source === "external_send" && (
                                    <span className="inline-flex rounded-full bg-[#F0EBE1] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#5A4B2A] font-semibold"
                                          data-testid={`comms-notes-row-${r.ref_id || idx}-ext-badge`}>
                                        Sent externally
                                    </span>
                                )}
                                {m.direction && <><span>·</span><span>{m.direction}</span></>}
                                {m.channel && <><span>·</span><span>{m.channel}</span></>}
                                {m.message_class && <><span>·</span><span>{m.message_class}</span></>}
                                <span className="ml-auto">{fmt(r.ts)}</span>
                            </div>
                            {r.type === "email" ? (
                                <>
                                    <div className="font-semibold text-charcoal text-[14px]">{m.subject || (m.source === "external_send" ? `External send · ${m.template_code || "communication"}` : "(no subject)")}</div>
                                    {m.body && <div className="text-[13px] text-[#333] whitespace-pre-wrap">{m.body}</div>}
                                    {m.source === "external_send" && m.sent_externally_notes && (
                                        <div className="text-[12px] text-[#555] italic" data-testid={`comms-notes-row-${r.ref_id || idx}-ext-notes`}>
                                            Notes: {m.sent_externally_notes}
                                        </div>
                                    )}
                                    {(m.linked_file_ids||[]).length>0 && <div className="text-[12px] text-[#666]">Attachments: {m.linked_file_ids.length}</div>}
                                    {m.provider_message_id && <div className="text-[11px] text-[#999]">Provider id · {m.provider_message_id}</div>}
                                </>
                            ) : (
                                <div className="text-[13px] text-[#333] whitespace-pre-wrap break-words">
                                    {m.body || <span className="text-[#999]">—</span>}
                                </div>
                            )}
                            {r.by && <div className="text-[11px] text-[#999]">By {r.type === "note" ? (r.by_display_name || r.by) : r.by}</div>}
                        </li>);
                    })}
                </ul>}
            </div>
        </div>
    );
}

export function ProjectActivity() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    usePageMeta({ title: "Activity · Project | Studio G4 Admin" });
    useEffect(()=>{ (async ()=>{ try{ const r = await api.get(`/admin/projects/${id}/activity`); setItems(r.data?.items||[]); } finally { setLoading(false); }})(); }, [id]);
    return (
        <div className="flex flex-col gap-4" data-testid="activity-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="activity-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Activity</h1>
            <div className="text-[12px] text-[#666] italic">Append-only, non-editable audit stream.</div>
            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading…</div> :
                items.length===0 ? <div className="p-4 text-[#666] text-[13px]" data-testid="activity-empty">No activity yet.</div> :
                <ul className="flex flex-col divide-y divide-[#E5E2DC]">
                    {items.map(a=>(
                        <li key={a.id} data-testid={`activity-row-${a.id}`} className="p-3 flex items-start gap-3">
                            <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide bg-[#EBE8E0] text-charcoal rounded px-2 py-0.5">{a.event_type||"event"}</span>
                            <div className="flex-1">
                                <div className="text-[13px] text-charcoal">{a.description||"—"}</div>
                                <div className="text-[11px] text-[#999]">{a.actor_id ? `by ${a.actor_id}` : ""} {a.workstage?`· WS ${a.workstage}`:""}</div>
                            </div>
                            <div className="text-[11px] text-[#999] whitespace-nowrap">{fmt(a.created_at)}</div>
                        </li>
                    ))}
                </ul>}
            </div>
        </div>
    );
}
